import { DestroyRef, effect, inject, Injectable, DOCUMENT } from '@angular/core';

import { AppWindow } from '@shared/types';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { AuthService } from '@modules/components/auth/auth.service';
import { I18nLanguage } from '@shared/language/language.service';
import { LoggedUserDto } from '@shared/services/auth/dto/login.dto';
import UsersApiService from '@shared/services/users/users-api.service';
import { WidgetEvent, WidgetEventsService } from '../widget-events.service';

declare const window: AppWindow;

export interface HelpCrunchWidgetSettings {
  organization: string;
  appId: string;
  user?: object;
}

export type HelpCrunchWidget = (
  eventOrCommand: string,
  dataOrCallback?: object | string | (() => void),
) => void;

export interface HelpCrunchContact {
  fullName: string;
  email: string;
  phone: string;
  role: string;
  signupDate: string;
  market: string;
}

@Injectable({
  providedIn: 'root',
})
export default class HelpCrunchWidgetService {
  private readonly document = inject(DOCUMENT);
  private readonly authService = inject(AuthService);
  private readonly router: Router = inject(Router);
  private readonly usersApiService = inject(UsersApiService);
  private readonly widgetEventsService = inject(WidgetEventsService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly helpCrunchWidgetSettings: HelpCrunchWidgetSettings =
    environment.helpCrunchWidgetSettings;

  private isWidgetInitialized = false;

  constructor() {
    this.subscribeToWidgetEvents();

    effect(() => {
      const user = this.authService.user;
      if (!this.authService.isClient() && !this.authService.isTeacher()) {
        return;
      }
      if (!user && this.isWidgetInitialized) {
        this.helpCrunch('logout');
        return;
      }
      if (!this.isWidgetInitialized) {
        this.setUpWidgetForUser(user);
      } else {
        this.updateWidgetUser(user);
      }
    });
  }

  private async updateWidgetUser(user: LoggedUserDto): Promise<void> {
    const contact = await this.getUserForHelpcrunch(user);
    const helpcrunchUser = {
      user_id: user.id,
      name: contact.fullName,
      email: contact.email,
      phone: contact.phone,
      customData: {
        role: contact.role,
        registrationDate: contact.signupDate,
        market: contact.market,
      },
    };
    this.helpCrunch('updateUser', helpcrunchUser);
  }

  private async setUpWidgetForUser(user: LoggedUserDto): Promise<void> {
    const contact = await this.getUserForHelpcrunch(user);
    window.helpcrunchSettings = {
      ...this.helpCrunchWidgetSettings,
      user: {
        user_id: user.id,
        name: contact.fullName,
        email: contact.email,
        phone: contact.phone,
        customData: {
          role: contact.role,
          registrationDate: contact.signupDate,
          market: contact.market,
        },
      },
    };
    this.helpCrunchOriginalInitScript(window, this.document);
    this.helpCrunch('onReady', () => {
      this.router.events.subscribe(this.tryHideHelpCrunchWidget.bind(this));
      this.setLanguage(user?.language?.toLowerCase() || I18nLanguage.EN);
      this.tryHideHelpCrunchWidget();
      this.isWidgetInitialized = true;
    });
  }

  private async getUserForHelpcrunch(
    user: LoggedUserDto,
  ): Promise<HelpCrunchContact> {
    try {
      return await this.usersApiService
        .getContactInfoForHelpCrunchWidget(user.id)
        .toPromise();
    } catch (error) {
      console.error('Failed to fetch HelpCrunch contact info:', error);
      throw error;
    }
  }

  public setLanguage(language: string): void {
    this.helpCrunch('setPhraseList', language);
  }

  public showChatWidget(): void {
    if (!this.isWidgetInitialized) {
      return;
    }
    this.helpCrunch('openChat');
  }

  private subscribeToWidgetEvents(): void {
    this.widgetEventsService.events$.subscribe((event) => {
      if (event === WidgetEvent.OPEN_CHAT) {
        this.showChatWidget();
      }
    });
  }

  private tryHideHelpCrunchWidget(): void {
    if (this.authService.isTeacher() || this.authService.isClient()) {
      return;
    }

    this.helpCrunch('hideChatWidget');
  }

  private helpCrunch(
    event: string,
    dataOrCallback?: object | string | (() => void),
  ): void {
    window.HelpCrunch(event, dataOrCallback);
  }

  // eslint-disable-next-line
  private helpCrunchOriginalInitScript(w, d): void { var hS = w.helpcrunchSettings; if (!hS || !hS.organization) { return; } var widgetSrc = 'https://' + hS.organization + '.widget.helpcrunch.com/'; w.HelpCrunch = function () { w.HelpCrunch.q.push(arguments) }; w.HelpCrunch.q = []; function r() { if (d.querySelector('script[src="' + widgetSrc + '"')) { return; } var s = d.createElement('script'); s.async = 1; s.type = 'text/javascript'; s.src = widgetSrc; (d.body || d.head).appendChild(s); } if (d.readyState === 'complete' || hS.loadImmediately) { r(); } else if (w.attachEvent) { w.attachEvent('onload', r) } else { w.addEventListener('load', r, false) } }
}
